import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Plus, Save, Trash2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import MegaMenuModal from "./MegaMenuModal";
import MenuPageSummary from "./MenuPageSummary";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";
import { toast } from "react-toastify";
import "./Menus.css";

const Menus = () => {

    const url = API_BASE_URL;
    const [openAccordion, setOpenAccordion] = useState(null);
    const [linkType, setLinkType] = useState("text");
    const [customLink, setCustomLink] = useState({ label: "", url: "" });
    const [summaryPeriod, setSummaryPeriod] = useState("All Time");
    const [highlightMenuId, setHighlightMenuId] = useState(null);

    const [selectedPages, setSelectedPages] = useState([]);
    const [selectedPosts, setSelectedPosts] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [removingItems, setRemovingItems] = useState({});
    const [menuItems, setMenuItems] = useState([]);
    const [openMenuItem, setOpenMenuItem] = useState(null);
    const encrypted = getDecryptedUserFromCookie("gta_mainstateuser");
    const token = decryptToken("accessToken") || ""
    const [activeMegaMenu, setActiveMegaMenu] = useState(null);
    const [savingItems, setSavingItems] = useState({});
    const [addingMenu, setAddingMenu] = useState(false);

    const parseJsonResponse = async (response) => {
        const contentType = response.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) {
            const text = await response.text();
            throw new Error(
                response.ok
                  ? "Server returned an unexpected response."
                  : `Request failed (${response.status}). ${text.slice(0, 80)}`
            );
        }
        return response.json();
    };

    // Example data
    const pages = ["About Us", "Contact", "Services", "Blog"];
    const posts = ["Post One", "Post Two", "Post Three"];
    const [categories, setCategories] = useState([]);

    const toggleAccordion = (key) => {
        setOpenAccordion(openAccordion === key ? null : key);
    };

    const toggleSelection = (item, list, setList) => {
        setList(list.includes(item) ? list.filter((i) => i !== item) : [...list, item]);
    };



    const addCustomLink = () => {
        if (!customLink.label || !customLink.url) return;

        const newItem = { ...customLink, type: linkType };
        setMenuItems([...menuItems, newItem]);
        saveMenuItem(newItem);  // save to backend

        setCustomLink({ label: "", url: "" });
    };

    const addSelectedItems = (items, type, clearFn) => {
        const newItems = items.map((i) => ({ label: i, url: "#", type }));
        setMenuItems([...menuItems, ...newItems]);

        newItems.forEach((item) => saveMenuItem(item)); // save each item
        clearFn([]);
    };


    const updateMenuItem = (index, field, value) => {
        const updated = [...menuItems];
        updated[index][field] = value;
        setMenuItems(updated);
    };

    const removeMenuItem = async (index, id) => {
        if (!id) return;

        setRemovingItems(prev => ({ ...prev, [id]: true }));
        try {
            const response = await fetch(`${url}/menu/${id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            })
            const res = await response.json();
            if (response.ok) {
                toast.success("Removed")
                setMenuItems(menuItems.filter((_, i) => i !== index));
            }
        } catch (error) {
            console.error(error)
        } finally {
            setRemovingItems(prev => ({ ...prev, [id]: false }));
        }

    };


    const editMenuItem = async (item) => {
        setSavingItems(prev => ({ ...prev, [item.id]: true }));
        try {
            const response = await fetch(`${url}/menu/${item.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`, // if required
                },
                body: JSON.stringify({
                    label: item.label,
                    url: item.url,
                    image_url: item.image_url || null,
                    link_type: item.type || "text"
                }),
            });

            const res = await response.json();
            if (response.ok) {
                toast.success("updated")
            } else {
                toast.error(res?.message || "error occured")
            }
        } catch (error) {
            toast.error(error)
            console.error("Error updating menu:", error);
        } finally {
            setSavingItems(prev => ({ ...prev, [item.id]: false }));
        }
    };


    const saveMenuItem = async (item) => {
        if (addingMenu) return;
        setAddingMenu(true);
        try {
            const response = await fetch(`${url}/menu`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`, // if token is needed
                },
                body: JSON.stringify({
                    label: item.label,
                    url: item.url,
                    image_url: item.image_url || null,
                    link_type: item.type, // "text" or "image"
                }),
            });

            const res = await response.json();
            if (response.ok) {
                toast.success("Menu saved successfully");
                await fetchMenuItems();
            } else {
                toast.warning(res.messgage || "Error occured");
            }
        } catch (error) {
            toast.error(error.message);
            console.error("Error saving menu:", error);
        } finally {
            setAddingMenu(false);
        }
    };

    const fetchMenuItems = async () => {
        try {
            const response = await fetch(`${url}/menu`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await parseJsonResponse(response);
            if (!response.ok) {
                throw new Error(data?.message || "Failed to load menus");
            }
            setMenuItems(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error fetching menu:", error);
            setMenuItems([]);
        }
    };


    const menucategoryfetch = async () => {
        try {
            const response = await fetch(`${url}/category/getdata/category/${encrypted.main_id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const res = await parseJsonResponse(response);
            if (!response.ok) {
                throw new Error(res?.message || "Failed to load categories");
            }
            setCategories(Array.isArray(res) ? res : []);
        } catch (error) {
            console.error(error);
            setCategories([]);
        }
    };

    useEffect(() => {
        menucategoryfetch();
        fetchMenuItems();
    }, [])
    return (
        <div className="menus-page">
            <MenuPageSummary
                period={summaryPeriod}
                onPeriodChange={setSummaryPeriod}
                menuItems={menuItems}
                highlightMenuId={highlightMenuId}
                onHighlightMenuId={setHighlightMenuId}
            />

            <div className="menus-page__layout">
                {/* Left Sidebar */}
                <div className="menus-page__sidebar">
                    <h2 className="menus-page__heading">
                        Menu Builder
                    </h2>

                    {[
                        {
                            key: "custom",
                            title: "Custom Links",
                            content: (
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-[12px] font-medium text-gray-600 mb-1">
                                            Link Type
                                        </label>
                                        <div className="flex gap-4">
                                            <label className="flex items-center gap-1 text-sm text-gray-700">
                                                <input
                                                    type="radio"
                                                    value="text"
                                                    checked={linkType === "text"}
                                                    onChange={(e) => setLinkType(e.target.value)}
                                                />
                                                Text
                                            </label>
                                            <label className="flex items-center gap-1 text-sm text-gray-700">
                                                <input
                                                    type="radio"
                                                    value="image"
                                                    checked={linkType === "image"}
                                                    onChange={(e) => setLinkType(e.target.value)}
                                                />
                                                Image
                                            </label>
                                        </div>
                                    </div>

                                    {linkType === "text" && (
                                        <div>
                                            <label className="block text-[12px] font-medium text-gray-600 mb-1">
                                                Link Label
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full border rounded px-2 py-1 text-sm"
                                                value={customLink.label}
                                                onChange={(e) =>
                                                    setCustomLink({ ...customLink, label: e.target.value })
                                                }
                                            />
                                        </div>
                                    )}
                                    {linkType === "image" && (
                                        <div>
                                            <label className="block text-[12px] font-medium text-gray-600 mb-1">
                                                Image URL
                                            </label>
                                            <input
                                                type="text"
                                                className="w-full border rounded px-2 py-1 text-sm"
                                                value={customLink.label}
                                                onChange={(e) =>
                                                    setCustomLink({ ...customLink, label: e.target.value })
                                                }
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-[12px] font-medium text-gray-600 mb-1">
                                            Link URL
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full border rounded px-2 py-1 text-sm"
                                            value={customLink.url}
                                            onChange={(e) =>
                                                setCustomLink({ ...customLink, url: e.target.value })
                                            }
                                        />
                                    </div>


                                    <button
                                        onClick={addCustomLink}
                                        disabled={addingMenu}
                                        className="menus-page__add-btn"
                                    >
                                        <Plus size={14} />
                                        {addingMenu ? "Adding..." : "Add to Menu"}
                                    </button>

                                </div>
                            ),
                        },
                        {
                            key: "pages",
                            title: "Pages",
                            content: (
                                <div>
                                    {pages.map((page, idx) => (
                                        <label
                                            key={idx}
                                            className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedPages.includes(page)}
                                                onChange={() =>
                                                    toggleSelection(page, selectedPages, setSelectedPages)
                                                }
                                            />
                                            {page}
                                        </label>
                                    ))}

                                    <button
                                        onClick={() => addSelectedItems(selectedPages, "page", setSelectedPages)}
                                        disabled={addingMenu}
                                        className="menus-page__add-btn mt-3"
                                    >
                                        <Plus size={14} />
                                        {addingMenu ? "Adding..." : "Add to Menu"}
                                    </button>

                                </div>
                            ),
                        },
                        {
                            key: "posts",
                            title: "Posts",
                            content: (
                                <div>
                                    {posts.map((post, idx) => (
                                        <label
                                            key={idx}
                                            className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedPosts.includes(post)}
                                                onChange={() =>
                                                    toggleSelection(post, selectedPosts, setSelectedPosts)
                                                }
                                            />
                                            {post}
                                        </label>
                                    ))}

                                    <button
                                        onClick={() => addSelectedItems(selectedPosts, "post", setSelectedPosts)}
                                        disabled={addingMenu}
                                        className="menus-page__add-btn mt-3"
                                    >
                                        <Plus size={14} />
                                        {addingMenu ? "Adding..." : "Add to Menu"}
                                    </button>

                                </div>
                            ),
                        },
                        {
                            key: "categories",
                            title: "Categories",
                            content: (
                                <div>
                                    {categories.map((cat, idx) => (
                                        <label
                                            key={idx}
                                            className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:text-gray-900"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedCategories.includes(cat.category)}
                                                onChange={() =>
                                                    toggleSelection(
                                                        cat.category,
                                                        selectedCategories,
                                                        setSelectedCategories
                                                    )
                                                }
                                            />
                                            {cat.category}
                                        </label>
                                    ))}
                                    {/* <button
                                        onClick={() =>
                                            addSelectedItems(
                                                selectedCategories,
                                                "category",
                                                setSelectedCategories
                                            )
                                        }
                                        className="mt-3 w-full flex items-center justify-center gap-1 bg-gray-900 text-white rounded-md py-1.5 text-sm hover:bg-gray-700"
                                    >
                                        <Plus size={14} /> Add to Menu
                                    </button> */}
                                    <button
                                        onClick={() => addSelectedItems(selectedCategories, "category", setSelectedCategories)}
                                        disabled={addingMenu}
                                        className="menus-page__add-btn mt-3"
                                    >
                                        <Plus size={14} />
                                        {addingMenu ? "Adding..." : "Add to Menu"}
                                    </button>

                                </div>
                            ),
                        },
                    ].map((section) => (
                        <div key={section.key} className="mb-3">
                            <button
                                onClick={() => toggleAccordion(section.key)}
                                className="menus-page__accordion-btn"
                            >
                                {section.title}
                                {openAccordion === section.key ? (
                                    <ChevronDown size={14} />
                                ) : (
                                    <ChevronRight size={14} />
                                )}
                            </button>
                            <AnimatePresence>
                                {openAccordion === section.key && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                        className="menus-page__accordion-panel"
                                    >
                                        {section.content}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                </div>

                {/* Right Panel */}
                <div className="menus-page__main">
                    <h2 className="menus-page__heading">
                        Menu Preview
                    </h2>
                    {menuItems.length === 0 && (
                        <p className="menus-page__empty">No items added yet.</p>
                    )}
                    <div className="menus-page__menu-list">
                        {menuItems.map((item, idx) => (
                            <div
                                key={item.id || idx}
                                className={`menus-page__menu-card${
                                    highlightMenuId === item.id ? " menus-page__menu-card--highlight" : ""
                                }`}
                            >
                                <button
                                    onClick={() =>
                                        setOpenMenuItem(openMenuItem === idx ? null : idx)
                                    }
                                    className="menus-page__menu-card-head"
                                >
                                    {item.label}
                                    <div className="menus-page__menu-card-actions">
                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveMegaMenu(item);
                                            }}
                                            className="menus-page__mega-btn">
                                            Mega Menu
                                        </div>

                                        <div>
                                            {openMenuItem === idx ? (
                                                <ChevronDown size={14} />
                                            ) : (
                                                <ChevronRight size={14} />
                                            )}
                                        </div>
                                    </div>
                                </button>
                                <AnimatePresence>
                                    {openMenuItem === idx && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="menus-page__menu-card-body"
                                        >
                                            <div>
                                                <label className="menus-page__field-label">
                                                    Label
                                                </label>
                                                <input
                                                    type="text"
                                                    value={item.label}
                                                    onChange={(e) =>
                                                        updateMenuItem(idx, "label", e.target.value)
                                                    }
                                                    className="menus-page__field-input"
                                                />
                                            </div>
                                            <div>
                                                <label className="menus-page__field-label">
                                                    URL
                                                </label>
                                                <input
                                                    type="text"
                                                    value={item.url}
                                                    onChange={(e) =>
                                                        updateMenuItem(idx, "url", e.target.value)
                                                    }
                                                    className="menus-page__field-input"
                                                />
                                            </div>
                                            <div className="menus-page__card-actions">
                                                <button
                                                    onClick={() => removeMenuItem(idx, item.id)}
                                                    disabled={removingItems[item.id]}
                                                    className="menus-page__btn-remove"
                                                >
                                                    <Trash2 size={14} />
                                                    {removingItems[item.id] ? "Removing..." : "Remove"}
                                                </button>

                                                <button
                                                    onClick={() => editMenuItem(item)}
                                                    disabled={savingItems[item.id]}
                                                    className="menus-page__btn-save"
                                                >
                                                    <Save size={14} />
                                                    {savingItems[item.id] ? "Saving..." : "Save"}
                                                </button>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <MegaMenuModal
                isOpen={!!activeMegaMenu}
                onClose={() => setActiveMegaMenu(null)}
                menuItem={activeMegaMenu}
            />
        </div>
    );
};

export default Menus;
